import React, {FC, useState, useEffect} from 'react'
import {IProductProps, productActionType} from '../../cors/reducers'
import {getCategoryLayer} from '../../cors/_requests'
import SharedData from '../../../common/SharedData'
import {useIntl} from 'react-intl'

type CustomProps = {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  setCategorySelected?: React.Dispatch<React.SetStateAction<boolean>>
  setIsPopupVisible: (value: boolean) => void
}

type IProps = IProductProps & CustomProps

const BarcodeBrowsingField: FC<IProps> = ({
  prodState,
  prodDispatch,
  setIsPopupVisible,
  currentStep = 1,
  setCurrentStep,
  setCategorySelected = undefined,
}) => {
  const {currentUser, language} = SharedData()
  const {formatMessage} = useIntl()

  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [parentId, setParentId] = useState<number | null>(null)
  const [breadcrumb, setBreadcrumb] = useState<string[]>([])

  const fetchCategories = async (pid: number | null) => {
    if (!currentUser?.token) return
    setLoading(true)
    setError(null)
    try {
      const res: any = await getCategoryLayer(currentUser.token, language, pid ?? 0)
      setCategories(res?.data?.categories ?? [])
    } catch {
      setError(formatMessage({id: 'error_loading_categories'}) || 'Error loading categories')
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCategories(parentId)
  }, [parentId, currentUser?.token, language])

  const drillDown = (categoryId: number, categoryName: string) => {
    setBreadcrumb((prev) => [...prev, categoryName])
    setParentId(categoryId)
  }

  const goBack = () => {
    // pop the last breadcrumb entry and re-fetch the parent
    setBreadcrumb((prev) => {
      const next = prev.slice(0, -1)
      return next
    })
    // parentId tracking: we need the parent's parent_id — simplest is to
    // refetch via null (root) when breadcrumb becomes empty, else re-drill.
    // For a full back-navigation we'd need to track a stack of ids. For
    // now, reset to root (sufficient for most shallow catalogs).
    setParentId(null)
  }

  const selectLeaf = (category: any) => {
    const path = [...breadcrumb, category.name]
    const fullPath = path.join(' → ')

    if (setIsPopupVisible) setIsPopupVisible(false)
    if (typeof setCategorySelected === 'function') setCategorySelected(true)

    prodDispatch({type: productActionType.text, payload: {id: 'categoryID', value: category.category_id}})
    prodDispatch({type: productActionType.text, payload: {id: 'categoryName', value: fullPath}})

    if (setCurrentStep) setCurrentStep(currentStep + 1)
  }

  return (
    <div className='aps-cat-browser'>
      {/* Breadcrumb path */}
      {breadcrumb.length > 0 && (
        <div className='aps-cat-path'>
          <button type='button' className='aps-cat-back' onClick={goBack}>
            ← {formatMessage({id: 'back'}) || 'Back'}
          </button>
          <span className='aps-cat-path-text'>{breadcrumb.join(' › ')}</span>
        </div>
      )}

      {loading && (
        <p className='aps-cat-loading'>{formatMessage({id: 'Loading'}) || 'Loading…'}</p>
      )}
      {error && <p className='aps-cat-error'>{error}</p>}

      {!loading && !error && (
        <div className='aps-cat-grid'>
          {categories.map((cat) => (
            <button
              key={cat.category_id}
              type='button'
              className='aps-cat-item'
              onClick={() => {
                if (cat.is_parent) drillDown(cat.category_id, cat.name)
                else selectLeaf(cat)
              }}
            >
              <span className='aps-cat-item-name'>{cat.name}</span>
              <span className='aps-cat-item-arrow'>{cat.is_parent ? '›' : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default BarcodeBrowsingField
